import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ChevronDown, ChevronRight, Loader, Plus, Receipt, X } from 'lucide-react';
import { api, getApiErrorMessage } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { CustomerTypeahead, type PickedCustomer } from '../../components/sales/CustomerTypeahead';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { unwrapArray } from '../../lib/api-envelope';
import { todayCalendarDate } from '../../lib/calendar-date';
import { fromDatetimeLocal, toDatetimeLocal } from '../../lib/datetime-local';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { FOLLOW_UP_CHIPS, SALES_ACTIVITY_KIND_LABELS } from '../../lib/sales-activity';
import {
  SALES_ACTIVITY_KINDS,
  type SalesActivity,
  type SalesActivityAttendeeInput,
  type SalesActivityCreateBody,
  type SalesActivityDetail,
  type SalesActivityKind,
  type SalesActivityUpdateBody,
} from '../../types/sales-activity';

/**
 * /sales/meetings/new — the quick form — and /sales/meetings/:meetingId/edit.
 *
 * BUILT FOR A PHONE IN A TRUCK. Visible from the start: customer, when, notes,
 * next step, follow-up. Everything else is under "More". The happy path is
 * pick a customer, type (or dictate) the notes, Save.
 *
 * Body is snake_case (POST /sales-activities). `occurred_at` is an INSTANT
 * built from the picker as the TENANT's wall clock (fromDatetimeLocal), and
 * `follow_up_date` is a CALENDAR DAY built from the tenant's today — never
 * from `toISOString()`, which is UTC's day and runs a day late in Alberta
 * every evening.
 *
 * Opened with ?customer=<id> (from a customer page) or ?opportunity=<id>
 * (from an opportunity), those arrive pre-filled.
 */
const inputClass =
  'w-full rounded-lg border border-fieldiq-border bg-fieldiq-dark px-3 py-2.5 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none';
const labelClass = 'mb-1 block text-xs font-semibold text-fieldiq-text';

type AttendeeDraft = { key: string; input: SalesActivityAttendeeInput; label: string };

export default function MeetingFormScreen() {
  const { meetingId } = useParams<{ meetingId: string }>();
  const isEdit = !!meetingId;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const timeZone = useTenantTimeZone();

  const [customer, setCustomer] = useState<PickedCustomer | null>(null);
  const [when, setWhen] = useState('');
  const [initialWhen, setInitialWhen] = useState('');
  const [notes, setNotes] = useState('');
  const [nextStep, setNextStep] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [moreOpen, setMoreOpen] = useState(false);
  const [kind, setKind] = useState<SalesActivityKind>('meeting');
  const [contactId, setContactId] = useState('');
  const [rig, setRig] = useState('');
  const [location, setLocation] = useState('');
  const [attendees, setAttendees] = useState<AttendeeDraft[]>([]);
  const [attendeeName, setAttendeeName] = useState('');
  const [opportunityId, setOpportunityId] = useState(searchParams.get('opportunity') ?? '');
  const [saved, setSaved] = useState<SalesActivity | null>(null);
  const [error, setError] = useState<string | null>(null);

  // "When" defaults to now, on the tenant's clock.
  useEffect(() => {
    if (!isEdit && !when && timeZone) setWhen(toDatetimeLocal(new Date().toISOString(), timeZone));
  }, [isEdit, when, timeZone]);

  // Pre-fill the customer from ?customer=.
  const customerParam = searchParams.get('customer');
  const prefillQuery = useQuery({
    queryKey: ['customer', customerParam],
    queryFn: () => api.customers.get(customerParam!).then((r) => r.data?.data ?? r.data),
    enabled: !isEdit && !!customerParam,
  });
  useEffect(() => {
    const c = prefillQuery.data;
    if (c && !customer) setCustomer({ id: c.id, name: c.name, status: c.status });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillQuery.data]);

  // Edit: load the meeting and fill the form once.
  const editQuery = useQuery({
    queryKey: ['sales-activity', meetingId],
    queryFn: () => api.salesActivities.get(meetingId!).then((r) => r.data.data as SalesActivityDetail),
    enabled: isEdit,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const editFailure = queryLoadFailure(editQuery);
  const [filled, setFilled] = useState(false);
  useEffect(() => {
    const m = editQuery.data;
    if (!m || filled || !timeZone) return;
    setCustomer({ id: m.customerId, name: m.customerName ?? '—', status: m.customerStatus });
    const w = toDatetimeLocal(m.occurredAt, timeZone);
    setWhen(w);
    setInitialWhen(w);
    setNotes(m.notes ?? '');
    setNextStep(m.nextStep ?? '');
    setFollowUp(m.followUpDate ?? '');
    setKind((SALES_ACTIVITY_KINDS as readonly string[]).includes(m.kind) ? (m.kind as SalesActivityKind) : 'other');
    setContactId(m.contactId ?? '');
    setRig(m.rigName ?? '');
    setLocation(m.location ?? '');
    setOpportunityId(m.opportunityId ?? '');
    setAttendees(
      (m.attendees ?? []).map((a) => ({
        key: a.id,
        input: a.userId ? { user_id: a.userId } : a.contactId ? { contact_id: a.contactId } : { name: a.name ?? a.displayName },
        label: a.displayName,
      })),
    );
    if (m.contactId || m.rigName || m.location || (m.attendees ?? []).length || m.opportunityId || m.kind !== 'meeting') setMoreOpen(true);
    setFilled(true);
  }, [editQuery.data, filled, timeZone]);

  const customerId = customer?.id ?? '';

  // The customer's contacts (sales may read them), then narrowed to the rig.
  const contactsQuery = useQuery({
    queryKey: ['customer-contacts', customerId],
    queryFn: () => api.customers.listContacts(customerId).then((r) => unwrapArray<any>(r.data)),
    enabled: !!customerId,
  });
  const contacts = useMemo(() => (contactsQuery.data ?? []) as any[], [contactsQuery.data]);
  const rigs = useMemo(
    () => Array.from(new Set(contacts.map((c) => c.rigName).filter(Boolean))) as string[],
    [contacts],
  );
  const contactOptions = useMemo(() => {
    const r = rig.trim().toLowerCase();
    return r ? contacts.filter((c) => (c.rigName ?? '').toLowerCase() === r) : contacts;
  }, [contacts, rig]);

  const opportunitiesQuery = useQuery({
    queryKey: ['opportunities', 'for-customer', customerId],
    queryFn: () =>
      api.opportunities.list({ customer_id: customerId, stage: 'lead,qualified,quoted' }).then((r) => unwrapArray<any>(r.data)),
    enabled: !!customerId,
  });
  const opportunities = (opportunitiesQuery.data ?? []) as any[];

  // A new customer invalidates everything that hung off the old one.
  const pickCustomer = (c: PickedCustomer | null) => {
    setCustomer(c);
    setContactId('');
    setAttendees((a) => a.filter((x) => !('contact_id' in x.input)));
    if (!searchParams.get('opportunity')) setOpportunityId('');
  };

  const today = todayCalendarDate(timeZone);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const occurredAt = fromDatetimeLocal(when, timeZone);
      if (isEdit) {
        const body: SalesActivityUpdateBody = {
          kind,
          contact_id: contactId || null,
          rig_name: rig.trim() || null,
          location: location.trim() || null,
          notes: notes.trim() || null,
          next_step: nextStep.trim() || null,
          follow_up_date: followUp || null,
          attendees: attendees.map((a) => a.input),
          opportunity_id: opportunityId || null,
        };
        // Sent only when the picker string moved: a round trip through the
        // minute-resolution picker would otherwise truncate the seconds.
        if (when !== initialWhen && occurredAt) body.occurred_at = occurredAt;
        return api.salesActivities.update(meetingId!, body).then((r) => r.data.data as SalesActivity);
      }
      const body: SalesActivityCreateBody = { customer_id: customerId, kind };
      if (occurredAt) body.occurred_at = occurredAt;
      if (notes.trim()) body.notes = notes.trim();
      if (nextStep.trim()) body.next_step = nextStep.trim();
      if (followUp) body.follow_up_date = followUp;
      if (contactId) body.contact_id = contactId;
      if (rig.trim()) body.rig_name = rig.trim();
      if (location.trim()) body.location = location.trim();
      if (attendees.length) body.attendees = attendees.map((a) => a.input);
      if (opportunityId) body.opportunity_id = opportunityId;
      return api.salesActivities.create(body).then((r) => r.data.data as SalesActivity);
    },
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ['sales-activities'] });
      queryClient.invalidateQueries({ queryKey: ['sales-activity', row?.id ?? meetingId] });
      if (isEdit) navigate(`/sales/meetings/${meetingId}`, { replace: true });
      else setSaved(row);
    },
    onError: (err) => setError(getApiErrorMessage(err, 'Could not save this meeting.')),
  });

  const canSave = !!customerId && !!when && !!timeZone && !saveMutation.isPending;

  const resetForAnother = () => {
    setSaved(null);
    setCustomer(null);
    setWhen(timeZone ? toDatetimeLocal(new Date().toISOString(), timeZone) : '');
    setNotes(''); setNextStep(''); setFollowUp(''); setKind('meeting'); setContactId('');
    setRig(''); setLocation(''); setAttendees([]); setOpportunityId(''); setError(null); setMoreOpen(false);
  };

  const band = (
    <PageHeaderBand
      eyebrow="Sales"
      title={isEdit ? 'Edit meeting' : 'Log a meeting'}
      onBack={() => navigate(-1)}
      backLabel="Back"
    />
  );

  if (saved) {
    const receiptHref = `/expenses/new?kind=general&customer=${encodeURIComponent(saved.customerId)}&meeting=${encodeURIComponent(saved.id)}`;
    return (
      <div data-testid="meeting-form-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        {band}
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
          <div className="mx-auto max-w-[1920px] space-y-4">
            <div role="status" className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-5">
              <p className="flex items-center gap-2 text-base font-bold text-white">
                <CheckCircle2 className="size-5 text-fieldiq-success" aria-hidden="true" />
                Meeting logged with {saved.customerName ?? customer?.name ?? 'the customer'}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to={receiptHref} className="fi-action fi-action--standard fi-action--primary">
                  <Receipt className="size-4" aria-hidden="true" />
                  Add a receipt for this meeting
                </Link>
                <Link to={`/sales/meetings/${saved.id}`} className="fi-action fi-action--standard border border-fieldiq-border bg-fieldiq-card text-fieldiq-text">
                  View meeting
                </Link>
                <button type="button" onClick={resetForAnother} className="fi-action fi-action--standard border border-fieldiq-border bg-fieldiq-card text-fieldiq-text">
                  Log another
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (isEdit && editFailure) {
    return (
      <div data-testid="meeting-form-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        {band}
        <LoadFailureNotice error={queryFailureError(editQuery)} subject="this meeting" />
      </div>
    );
  }

  return (
    <div data-testid="meeting-form-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      {band}
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <form
          className="mx-auto max-w-[1920px] space-y-4"
          onSubmit={(e) => { e.preventDefault(); if (canSave) { setError(null); saveMutation.mutate(); } }}
        >
          <div className="space-y-4 rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4">
            <div>
              <label htmlFor="meeting-customer" className={labelClass}>Customer *</label>
              <CustomerTypeahead value={customer} onChange={pickCustomer} disabled={isEdit} />
            </div>

            <div>
              <label htmlFor="meeting-when" className={labelClass}>When</label>
              <input id="meeting-when" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className={inputClass} />
            </div>

            <div>
              <label htmlFor="meeting-notes" className={labelClass}>Notes</label>
              <textarea id="meeting-notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputClass} placeholder="What was discussed" />
            </div>

            <div>
              <label htmlFor="meeting-next-step" className={labelClass}>Next step</label>
              <input id="meeting-next-step" value={nextStep} onChange={(e) => setNextStep(e.target.value)} className={inputClass} placeholder="Send pricing, book a site visit…" />
            </div>

            <div>
              <label htmlFor="meeting-follow-up" className={labelClass}>Follow-up date</label>
              <div className="flex flex-wrap items-center gap-2">
                <input id="meeting-follow-up" type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} className={`${inputClass} w-auto flex-1 sm:flex-none`} />
                {FOLLOW_UP_CHIPS.map((chip) => {
                  const day = today ? chip.from(today) : null;
                  return (
                    <button
                      key={chip.key}
                      type="button"
                      disabled={!day}
                      onClick={() => day && setFollowUp(day)}
                      aria-pressed={!!day && followUp === day}
                      className={`rounded-fi-pill px-3 py-1.5 text-xs font-extrabold ${
                        day && followUp === day ? 'bg-fi-amber text-fi-amber-ink' : 'border border-fieldiq-border bg-fieldiq-dark text-fieldiq-text'
                      }`}
                    >
                      {chip.label}
                    </button>
                  );
                })}
                {followUp && (
                  <button type="button" onClick={() => setFollowUp('')} aria-label="Clear follow-up date" className="text-fieldiq-muted hover:text-white">
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>

            <button
              type="button"
              aria-expanded={moreOpen}
              onClick={() => setMoreOpen((o) => !o)}
              className="flex items-center gap-1 text-sm font-semibold text-fieldiq-text"
            >
              {moreOpen ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
              More
            </button>

            {moreOpen && (
              <div className="grid grid-cols-1 gap-4 border-t border-fieldiq-border pt-4 md:grid-cols-2">
                <div>
                  <label htmlFor="meeting-kind" className={labelClass}>Kind</label>
                  <select id="meeting-kind" value={kind} onChange={(e) => setKind(e.target.value as SalesActivityKind)} className={inputClass}>
                    {SALES_ACTIVITY_KINDS.map((k) => (
                      <option key={k} value={k}>{SALES_ACTIVITY_KIND_LABELS[k]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="meeting-rig" className={labelClass}>Rig</label>
                  <input id="meeting-rig" list="meeting-rig-options" value={rig} onChange={(e) => setRig(e.target.value)} className={inputClass} autoComplete="off" />
                  <datalist id="meeting-rig-options">
                    {rigs.map((r) => <option key={r} value={r} />)}
                  </datalist>
                </div>
                <div>
                  <label htmlFor="meeting-contact" className={labelClass}>Contact</label>
                  <select
                    id="meeting-contact"
                    value={contactId}
                    onChange={(e) => setContactId(e.target.value)}
                    disabled={!customerId}
                    className={inputClass}
                  >
                    <option value="">{customerId ? 'No contact' : 'Choose a customer first'}</option>
                    {contactOptions.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}{c.rigName ? ` · ${c.rigName}` : ''}</option>
                    ))}
                    {contactId && !contactOptions.some((c) => c.id === contactId) && (
                      <option value={contactId}>{contacts.find((c) => c.id === contactId)?.name ?? 'Current contact'}</option>
                    )}
                  </select>
                </div>
                <div>
                  <label htmlFor="meeting-location" className={labelClass}>Location</label>
                  <input id="meeting-location" value={location} onChange={(e) => setLocation(e.target.value)} className={inputClass} />
                </div>
                <div className="md:col-span-2">
                  <span className={labelClass}>Attendees</span>
                  <div className="flex flex-wrap gap-2">
                    {attendees.map((a) => (
                      <span key={a.key} className="inline-flex items-center gap-1 rounded-fi-pill border border-fieldiq-border px-2.5 py-1 text-xs text-fieldiq-text">
                        {a.label}
                        <button type="button" aria-label={`Remove ${a.label}`} onClick={() => setAttendees((xs) => xs.filter((x) => x.key !== a.key))}>
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {contacts
                      .filter((c) => !attendees.some((a) => 'contact_id' in a.input && a.input.contact_id === c.id))
                      .slice(0, 6)
                      .map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setAttendees((xs) => [...xs, { key: `c-${c.id}`, input: { contact_id: c.id }, label: c.name }])}
                          className="inline-flex items-center gap-1 rounded-fi-pill border border-dashed border-fieldiq-border px-2.5 py-1 text-xs text-fieldiq-muted hover:text-white"
                        >
                          <Plus size={12} aria-hidden="true" /> {c.name}
                        </button>
                      ))}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <input
                      aria-label="Add an attendee by name"
                      value={attendeeName}
                      onChange={(e) => setAttendeeName(e.target.value)}
                      placeholder="Someone else, by name"
                      className={inputClass}
                    />
                    <button
                      type="button"
                      disabled={!attendeeName.trim()}
                      onClick={() => {
                        const n = attendeeName.trim();
                        setAttendees((xs) => [...xs, { key: `n-${Date.now()}`, input: { name: n }, label: n }]);
                        setAttendeeName('');
                      }}
                      className="rounded-lg border border-fieldiq-border bg-fieldiq-dark px-3 text-sm font-semibold text-white disabled:opacity-50"
                    >
                      Add
                    </button>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="meeting-opportunity" className={labelClass}>Opportunity</label>
                  <select
                    id="meeting-opportunity"
                    value={opportunityId}
                    onChange={(e) => setOpportunityId(e.target.value)}
                    disabled={!customerId}
                    className={inputClass}
                  >
                    <option value="">{customerId ? 'None' : 'Choose a customer first'}</option>
                    {opportunities.map((o) => (
                      <option key={o.id} value={o.id}>{o.title}</option>
                    ))}
                    {opportunityId && !opportunities.some((o) => o.id === opportunityId) && (
                      <option value={opportunityId}>The opportunity you came from</option>
                    )}
                  </select>
                </div>
              </div>
            )}

            {error && (
              <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-fieldiq-text">{error}</p>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 text-sm text-fieldiq-text">Cancel</button>
            <button type="submit" disabled={!canSave} className="fi-action fi-action--standard fi-action--primary disabled:opacity-50">
              {saveMutation.isPending && <Loader size={14} className="animate-spin" aria-hidden="true" />}
              {isEdit ? 'Save changes' : 'Save meeting'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
